/**
 * /system — skallet for systemadminpanelet (#178, epic #167 D3/D8).
 *
 * Utenfor familieappens skall: ingen NavShell, ingen hvilemodus og ingen
 * familiedata. Uten gyldig systemadminøkt (`GET /api/system/auth/me` → 401)
 * sendes man til /system/logg-inn. Her står hvem som er innlogget,
 * gjenopprettingskodene som er igjen, utlogging og familiene (FF-11, #179:
 * opprett, lenker, kode, suspender, slett) og statistikken (FF-12, #180).
 */
import type { ReactElement } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives';
import { asApiError } from '../../lib/api';
import { SystemFamiliesPanel } from './SystemFamiliesPanel';
import { SystemStatsPanel } from './SystemStatsPanel';
import { SITE_ADMIN_ME_KEY, SYSTEM_LOGIN_PATH, SystemApi } from './systemApi';

/** Under så mange ubrukte gjenopprettingskoder vises en advarsel. */
const LOW_RECOVERY_CODES = 3;

export function SystemShellRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: SITE_ADMIN_ME_KEY,
    queryFn: SystemApi.me,
    retry: false,
    staleTime: 0,
  });

  if (me.isPending) {
    return (
      <main data-route="system" className="flex min-h-dvh items-center justify-center p-12">
        <p data-testid="system-loading" className="text-ink-2 text-sm">
          {t('system.loading')}
        </p>
      </main>
    );
  }
  if (me.isError) {
    if (asApiError(me.error).status === 401) return <Navigate to={SYSTEM_LOGIN_PATH} replace />;
    return (
      <main data-route="system" className="flex min-h-dvh items-center justify-center p-12">
        <p data-testid="system-error" role="alert" className="text-bad text-sm">
          {t('system.error')}
        </p>
      </main>
    );
  }

  async function logout(): Promise<void> {
    try {
      await SystemApi.logout();
    } finally {
      qc.removeQueries({ queryKey: SITE_ADMIN_ME_KEY });
      navigate(SYSTEM_LOGIN_PATH, { replace: true });
    }
  }

  const admin = me.data;
  return (
    <main data-route="system" className="flex min-h-dvh flex-col">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4 px-6 pt-10">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-heading-m font-extrabold text-ink">{t('system.title')}</h1>
          <p data-testid="system-signed-in-as" className="m-0 text-sm text-ink-2">
            {t('system.signed_in_as', { username: admin.username })}
          </p>
        </div>
        <Button variant="secondary" className="shrink-0 whitespace-nowrap" data-testid="system-logout" onClick={() => void logout()}>
          {t('system.logout')}
        </Button>
      </header>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-6 py-8">
        {admin.recovery_codes_left < LOW_RECOVERY_CODES ? (
          <p
            data-testid="system-recovery-low"
            role="status"
            className="m-0 rounded-md bg-warn-bg px-3.5 py-3 text-sm font-bold text-ink"
          >
            {t('system.recovery_low', { count: admin.recovery_codes_left })}
          </p>
        ) : null}
        <SystemStatsPanel />
        <SystemFamiliesPanel />
      </div>
    </main>
  );
}
